import { forwardRef, useEffect } from 'react';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

/**
 * Çekirdek arayüz parçaları. Bir tasarım sistemi değil, tekrar eden beş-altı
 * kalıbın tek yerde tutulması.
 *
 * Tek stil kuralı: Tailwind sınıfı + index.css'teki :root tokenleri. Inline
 * `style` yalnızca gerçekten hesaplanmış değerler için. (snak'ta iki sistem
 * karışıktı ve tema değişkeni tek yerden değiştirilemiyordu.)
 */

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// ───────────────────────────────────────────────────────────────── Button

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'text-white bg-[var(--c-brand)] hover:bg-[var(--c-brand-hover)] border-transparent',
  secondary:
    'text-[var(--c-ink)] bg-[var(--c-surface)] hover:bg-[var(--c-surface-alt)] border-[var(--c-border-strong)]',
  danger: 'text-white bg-[var(--c-danger)] hover:brightness-110 border-transparent',
  ghost:
    'text-[var(--c-ink-soft)] bg-transparent hover:bg-[var(--c-surface-alt)] border-transparent',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, disabled, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      // İş sürerken tıklama engellenir — çift kayıt, çift fatura üretmesin.
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-[var(--r-md)] border font-medium',
        'transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
        size === 'sm' ? 'px-2.5 py-1 text-[13px]' : 'px-3.5 py-2 text-sm',
        BUTTON_VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {loading && <Spinner size={14} />}
      {children}
    </button>
  );
});

// ───────────────────────────────────────────────────────────────── Field

interface FieldShellProps {
  label?: string;
  hint?: string;
  error?: string | null;
  htmlFor?: string;
  children: ReactNode;
}

export function FieldShell({ label, hint, error, htmlFor, children }: FieldShellProps) {
  return (
    <label className="block" htmlFor={htmlFor}>
      {label && (
        <span className="mb-1 block text-[13px] font-medium text-[var(--c-ink)]">{label}</span>
      )}
      {children}
      {hint && !error && (
        <span className="mt-1 block text-xs text-[var(--c-ink-faint)]">{hint}</span>
      )}
      {error && <span className="mt-1 block text-xs text-[var(--c-danger)]">{error}</span>}
    </label>
  );
}

const CONTROL_CLASS =
  'w-full rounded-[var(--r-md)] border bg-[var(--c-surface)] px-2.5 py-1.5 text-sm ' +
  'text-[var(--c-ink)] placeholder:text-[var(--c-ink-faint)] transition-colors ' +
  'disabled:bg-[var(--c-surface-alt)] disabled:text-[var(--c-ink-soft)]';

function borderClass(error?: string | null): string {
  return error ? 'border-[var(--c-danger)]' : 'border-[var(--c-border-strong)]';
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string | null;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, className, ...rest },
  ref,
) {
  return (
    <FieldShell label={label} hint={hint} error={error}>
      <input
        ref={ref}
        /*
         * number alanında tekerlek koruması: imleç alanın üzerindeyken sayfa
         * kaydırılırsa tarayıcı DEĞERİ sessizce değiştirir. Bir tutar alanında
         * bu, fark edilmeyen yanlış rakam demektir.
         */
        onWheel={(e) => {
          if (e.currentTarget.type === 'number') e.currentTarget.blur();
        }}
        className={cx(CONTROL_CLASS, borderClass(error), className)}
        {...rest}
      />
    </FieldShell>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string | null;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, className, rows = 3, ...rest },
  ref,
) {
  return (
    <FieldShell label={label} hint={hint} error={error}>
      <textarea
        ref={ref}
        rows={rows}
        className={cx(CONTROL_CLASS, borderClass(error), 'resize-y', className)}
        {...rest}
      />
    </FieldShell>
  );
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  hint?: string;
  error?: string | null;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, className, children, ...rest },
  ref,
) {
  return (
    <FieldShell label={label} hint={hint} error={error}>
      <select ref={ref} className={cx(CONTROL_CLASS, borderClass(error), className)} {...rest}>
        {children}
      </select>
    </FieldShell>
  );
});

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string;
  hint?: string;
}

export function Checkbox({ label, hint, className, ...rest }: CheckboxProps) {
  return (
    <label className="flex cursor-pointer items-start gap-2 py-1">
      <input
        type="checkbox"
        className={cx('mt-0.5 h-4 w-4 accent-[var(--c-brand)]', className)}
        {...rest}
      />
      <span>
        <span className="block text-sm text-[var(--c-ink)]">{label}</span>
        {hint && <span className="block text-xs text-[var(--c-ink-faint)]">{hint}</span>}
      </span>
    </label>
  );
}

// ───────────────────────────────────────────────────────────────── Yerleşim

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <section
      className={cx(
        'rounded-[var(--r-lg)] border border-[var(--c-border)] bg-[var(--c-surface)]',
        'shadow-[var(--sh-sm)]',
        className,
      )}
    >
      {children}
    </section>
  );
}

export function CardHeader({ title, description }: { title: string; description?: string }) {
  return (
    <header className="border-b border-[var(--c-border)] px-4 py-3">
      <h2 className="text-sm font-semibold text-[var(--c-ink)]">{title}</h2>
      {description && <p className="mt-0.5 text-xs text-[var(--c-ink-soft)]">{description}</p>}
    </header>
  );
}

/**
 * GELİŞMİŞ BÖLÜM SARMALAYICISI — soluk gri gösterir, tercih AÇIKKEN TAMAMEN
 * gizler.
 *
 * ⚠⚠ BU BİR YETKİ KAPISI DEĞİL: veri/API hiçbir şekilde etkilenmez, yalnız
 * panel görünürlüğü. `hidden` true iken içerik DOM'a hiç binmiyor — form
 * state'i bilerek sıfırlıyor, çünkü yarım kalmış bir form gizlendiğinde
 * askıda kalmamalı; operatör geri açtığında bölümü baştan görür.
 *
 * ⚠ SOLUK HÂLDE BİLE TAM İŞLEVSEL: opaklık yalnız istirahat durumunda düşük,
 * `hover`/`focus-within`de tama döner — "gri = devre dışı" izlenimi
 * VERİLMEMELİ, çünkü değil.
 */
export function AdvancedSection({ hidden, children }: { hidden: boolean; children: ReactNode }) {
  if (hidden) return null;
  return (
    <div className="group/adv relative opacity-55 transition-opacity duration-150 hover:opacity-100 focus-within:opacity-100">
      <span className="pointer-events-none absolute -top-2 left-3 z-10 rounded-full border border-[var(--c-border)] bg-[var(--c-surface)] px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[var(--c-ink-faint)] opacity-0 transition-opacity group-hover/adv:opacity-100 group-focus-within/adv:opacity-100">
        Gelişmiş — Ayarlar → Görünüm'den gizlenebilir
      </span>
      {children}
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold text-[var(--c-ink)]">{title}</h1>
        {description && <p className="mt-1 text-sm text-[var(--c-ink-soft)]">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </header>
  );
}

// ───────────────────────────────────────────────────────────────── Durum

const BADGE_TONES = {
  neutral: 'bg-[var(--c-surface-alt)] text-[var(--c-ink-soft)]',
  ok: 'bg-[var(--c-ok-soft)] text-[var(--c-ok)]',
  warn: 'bg-[var(--c-warn-soft)] text-[var(--c-warn)]',
  danger: 'bg-[var(--c-danger-soft)] text-[var(--c-danger)]',
  brand: 'bg-[var(--c-brand-soft)] text-[var(--c-brand)]',
} as const;

export type BadgeTone = keyof typeof BADGE_TONES;

export function Badge({ tone = 'neutral', children }: { tone?: BadgeTone; children: ReactNode }) {
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
        BADGE_TONES[tone],
      )}
    >
      {children}
    </span>
  );
}

export function Spinner({ size = 16 }: { size?: number }) {
  return (
    <span
      role="status"
      aria-label="Yükleniyor"
      style={{ width: size, height: size, borderWidth: Math.max(2, Math.round(size / 8)) }}
      className="inline-block animate-spin rounded-full border-current border-t-transparent opacity-70"
    />
  );
}

export function EmptyState({ title, description }: { title: string; description?: string }) {
  return (
    <div className="px-4 py-10 text-center">
      <p className="text-sm font-medium text-[var(--c-ink)]">{title}</p>
      {description && <p className="mt-1 text-xs text-[var(--c-ink-soft)]">{description}</p>}
    </div>
  );
}

export function ErrorState({ message, detail }: { message: string; detail?: string | null }) {
  return (
    <div className="px-4 py-10 text-center">
      <p className="text-sm font-medium text-[var(--c-danger)]">{message}</p>
      {detail && <p className="mt-1 font-mono text-[11px] text-[var(--c-ink-faint)]">{detail}</p>}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────── Tablo

export function Table({ children }: { children: ReactNode }) {
  // Geniş tablolar KENDİ kabında yatay kayar; sayfa gövdesi asla yatay kaymaz.
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  );
}

export function Th({ children, align }: { children: ReactNode; align?: 'right' }) {
  return (
    <th
      className={cx(
        'border-b border-[var(--c-border)] px-3 py-2 text-xs font-semibold',
        'text-[var(--c-ink-soft)]',
        align === 'right' ? 'text-right' : 'text-left',
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  align,
  className,
  colSpan,
}: {
  children: ReactNode;
  align?: 'right';
  className?: string;
  /** Satır altına yayılan hata/açıklama satırları için. */
  colSpan?: number;
}) {
  return (
    <td
      colSpan={colSpan}
      className={cx(
        'border-b border-[var(--c-border)] px-3 py-2 align-top',
        align === 'right' ? 'tabular text-right' : '',
        className,
      )}
    >
      {children}
    </td>
  );
}

// ───────────────────────────────────────────────────────────────── Modal

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-[var(--r-lg)] bg-[var(--c-surface)] shadow-[var(--sh-lg)]"
      >
        <header className="flex items-center justify-between border-b border-[var(--c-border)] px-4 py-3">
          <h2 className="text-sm font-semibold">{title}</h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Kapat">
            ✕
          </Button>
        </header>
        <div className="px-4 py-4">{children}</div>
        {footer && (
          <footer className="flex justify-end gap-2 border-t border-[var(--c-border)] px-4 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}
